import type {
  AgentProductType,
  AgentVariantInventory,
  AgentVariantInventoryPatch,
  ProductStatus,
} from "schemas/generated/agentProduct";
import type {
  CatalogProduct,
  DesignGeometry,
  ProviderCustomization,
  ProviderMockupRequest,
  ProviderMockupTask,
} from "schemas/generated/productCatalog";
import type { RequestFn } from "./request";

export type CommerceProvidersResult = {
  providers: {
    key: string;
    displayName: string;
    /** Derived from the verbs the provider implements — never declared. */
    hasMockups: boolean;
  }[];
};

export type CommerceCatalogProductsInput = {
  provider: string;
  cursor?: string;
  categoryId?: string;
  limit?: number;
};

export type CommerceCatalogProductsResult = {
  products: CatalogProduct[];
  nextCursor: string | null;
};

export type CommerceCatalogProductResult = {
  product: CatalogProduct;
};

export type CommerceCatalogSyncInput = {
  projectId: string;
  provider: string;
  cursor?: string;
  limit?: number;
};

export type CommerceCatalogSyncResult = {
  items: { id: string; slug: string }[];
  nextCursor: string | null;
};

export type CommerceMockupTaskResult = {
  task: ProviderMockupTask;
};

export type CommerceAgentProduct = {
  id: string;
  title: string;
  description: string | null;
  slug: string | null;
  projectId: string;
  status: ProductStatus;
  type: AgentProductType;
  externalCheckoutUrl: string | null;
  stripeProductId: string | null;
  provider: string | null;
  providerProductId: string | null;
};

export type CommerceAgentProductOptionValue = {
  id: string;
  value: string;
  color: string | null;
  image: string | null;
};

export type CommerceAgentProductOption = {
  id: string;
  title: string;
  values: CommerceAgentProductOptionValue[];
};

/**
 * A removed option value. Returned flat rather than nested under its option, so
 * it carries the option a restore would put it back into.
 */
export type CommerceAgentProductRemovedOptionValue =
  CommerceAgentProductOptionValue & {
    optionId: string;
  };

export type CommerceAgentProductVariant = {
  id: string;
  sku: string | null;
  title: string | null;
  /** Integer minor units (Stripe convention) in `priceCurrencyCode` */
  price: number;
  originalPrice: number | null;
  priceCurrencyCode: string;
  /** Integer minor units (Stripe convention) in `unitCostCurrencyCode` */
  unitCost: number | null;
  unitCostCurrencyCode: string;
  providerVariantId: string | null;
  optionValueIds: string[];
  inventory: AgentVariantInventory;
};

export type CommerceAgentProductMedia = {
  id: string;
  assetId: string;
  url: string;
  sortOrder: number;
  variantId: string | null;
  optionValueId: string | null;
};

export type CommerceAgentProductFile = {
  id: string;
  url: string;
  name: string;
  sizeBytes: number;
  fileExtension: string;
  label: string | null;
  variantId: string | null;
  optionValueId: string | null;
  /**
   * Where the art sits in its area, in production-canvas units: the merchant's
   * placement on a `design:` row, the printed box on a `printfile:` row.
   */
  designGeometry: DesignGeometry | null;
  /** The library asset behind `url` — the canvas-safe way to re-read the bytes. */
  assetId: string | null;
};

export type CommerceAgentProductDetail = CommerceAgentProduct & {
  options: CommerceAgentProductOption[];
  variants: CommerceAgentProductVariant[];
  media: CommerceAgentProductMedia[];
  files: CommerceAgentProductFile[];
  /**
   * Removed option values a merchant can restore, plus the variants those
   * values composed, carrying the price/SKU/unit cost a restore brings back.
   * Kept separate from `options` / `variants` so those stay a list of sellable
   * rows only. Populated for provider-managed products.
   */
  removedOptionValues: CommerceAgentProductRemovedOptionValue[];
  removedVariants: CommerceAgentProductVariant[];
};

export type CommerceAgentProductsInput = {
  projectId: string;
  search?: string;
  status?: ProductStatus;
  cursor?: string;
  limit?: number;
  /** Hydrates `detailsByProductId` so list views skip a `get` per row. */
  withDetails?: boolean;
};

export type CommerceAgentProductsOverviewRange = "7d" | "30d" | "90d" | "all";

export type CommerceAgentProductsOverview = {
  netRevenue: number;
  orderCount: number;
  unitsSold: number;
  averageOrderValue: number;
  currencyCode: string | null;
};

export type CommerceAgentProductsResult = {
  items: CommerceAgentProduct[];
  nextCursor: string | null;
  soldCountByProductId: Record<string, number>;
  /**
   * Present when the request set `withDetails`. A product deleted between the
   * search and its hydration is omitted, so treat a missing key as gone.
   */
  detailsByProductId?: Record<string, CommerceAgentProductDetail>;
};

export type CommerceAgentProductResult = {
  product: CommerceAgentProduct;
};

export type CommerceAgentProductDetailResult = {
  product: CommerceAgentProductDetail;
};

export type CommerceCreateAgentProductResult = {
  product: CommerceAgentProductDetail;
  /**
   * False when the create was idempotent: the project already had this catalog
   * product and the call reconciled it. A caller rolling back its own failed
   * flow must not delete a product it did not create.
   */
  created: boolean;
};

export type CreateCommerceAgentProductInput = {
  /** Catalog provenance; the server loads the product document itself. */
  provider: string;
  providerProductId: string;
  customization?: ProviderCustomization | null;
  /**
   * Color names (matching the product's "Color" option values) to persist as
   * sellable. When omitted/null, every catalog variant is sold.
   */
  sellColorNames?: string[] | null;
};

export type CreateCustomCommerceAgentProductInput = {
  title: string;
  description?: string | null;
  status?: ProductStatus;
  type?: AgentProductType;
  externalCheckoutUrl?: string | null;
  options?: {
    name: string;
    values: {
      value: string;
      color?: string | null;
      imageUrl?: string | null;
    }[];
  }[];
  variants: {
    externalKey: string;
    title?: string | null;
    sku?: string | null;
    /** Integer minor units (Stripe convention); currency defaults to USD */
    price?: number;
    originalPrice?: number | null;
    priceCurrencyCode?: string;
    unitCost?: number | null;
    unitCostCurrencyCode?: string;
    optionValues: {
      optionName: string;
      value: string;
    }[];
  }[];
  media?: {
    url: string;
    name: string;
    altText?: string | null;
    label?: string | null;
    sortOrder?: number;
    variantExternalKey?: string | null;
    optionValue?: {
      optionName: string;
      value: string;
    } | null;
  }[];
};

export type UpdateCommerceAgentProductInput = {
  id: string;
  patch: {
    title?: string;
    description?: string | null;
    status?: ProductStatus;
    externalCheckoutUrl?: string | null;
  };
};

/**
 * Media entry for graph writes. Scope is flat over the wire: set
 * `variantExternalKey` for variant-scoped media, `optionValue` for
 * option-value-scoped media, or neither for product-scoped photos.
 */
export type CommerceAgentProductMediaInput = {
  url: string;
  name: string;
  altText?: string | null;
  label?: string | null;
  sortOrder?: number;
  variantExternalKey?: string | null;
  optionValue?: {
    optionName: string;
    value: string;
  } | null;
};

export type UpdateCommerceAgentProductGraphInput = {
  id: string;
  patch: {
    header?: {
      title?: string;
      description?: string | null;
      status?: ProductStatus;
      externalCheckoutUrl?: string | null;
    };
    variants?: {
      variantId: string;
      sku?: string | null;
      title?: string | null;
      /** Integer minor units (Stripe convention) in the variant's currency */
      price?: number;
      originalPrice?: number | null;
      priceCurrencyCode?: string;
      unitCost?: number | null;
      unitCostCurrencyCode?: string;
    }[];
    optionValues?: {
      optionValueId: string;
      value?: string;
      color?: string | null;
      image?: string | null;
    }[];
    /**
     * When present, replaces the product-scoped media list. Used by
     * provider-managed products to manage product photos without touching the
     * fulfillment-locked variant set.
     */
    media?: CommerceAgentProductMediaInput[];
    /**
     * Option value ids to drop, cascading to their variants. Lets a merchant
     * stop selling a colorway on a provider-managed product; removal is allowed
     * where renames and restructuring are not.
     */
    removedOptionValueIds?: string[];
    /**
     * Previously removed option value ids to bring back, together with every
     * variant they composed whose other option values are live. The inverse of
     * `removedOptionValueIds`, and just as safe on a fulfillment-locked set: it
     * only un-deletes rows the product already owns.
     */
    restoredOptionValueIds?: string[];
  };
};

/**
 * Per-variant inventory write: tracking flags plus an absolute stock count.
 * `compareAvailable` is the `available` the caller last read (null = none
 * stored); when present the stock write is compare-and-set and the API responds
 * 409 (`agentProduct.inventoryStale`) if the stored count moved since.
 */
export type SetCommerceAgentProductInventoryInput = {
  id: string;
  variants: (AgentVariantInventoryPatch & {
    variantId: string;
    compareAvailable?: number | null;
  })[];
};

/**
 * Full-graph reconcile for user-authored products: add/remove options, option
 * values, and variants. Rejected by the API for provider-managed products,
 * whose variant set is fulfillment-locked.
 */
export type ReplaceCommerceAgentProductGraphInput = {
  id: string;
  header: {
    title: string;
    description?: string | null;
    status?: ProductStatus;
    externalCheckoutUrl?: string | null;
  };
  options: {
    id?: string;
    name: string;
    values: {
      id?: string;
      value: string;
      color?: string | null;
      imageUrl?: string | null;
    }[];
  }[];
  variants: {
    id?: string;
    externalKey: string;
    title?: string | null;
    sku?: string | null;
    /** Integer minor units (Stripe convention); currency defaults to USD */
    price?: number;
    originalPrice?: number | null;
    priceCurrencyCode?: string;
    unitCost?: number | null;
    unitCostCurrencyCode?: string;
    optionValues: {
      optionName: string;
      value: string;
    }[];
  }[];
  media?: CommerceAgentProductMediaInput[];
  clearedVariantExternalKeys?: string[];
};

/**
 * Replaces the placement's stored customization: the design, print file, and
 * mockups previously saved for this placement are deleted and re-created from
 * this payload, except for the kept-print-file case documented on `printFile`.
 * A null design with no mockups clears the placement entirely.
 */
export type CreateCommerceAgentProductCustomizationInput = {
  productId: string;
  placement?: string | null;
  design?: {
    url: string;
    filename?: string | null;
    sizeBytes?: number | null;
    mimeType?: string | null;
    /** Where the design sits in its area, so re-opening restores the placement. */
    geometry?: DesignGeometry | null;
    /** The library asset the design was uploaded as, when it came from one. */
    assetId?: string | null;
  } | null;
  /**
   * The art as the factory must print it, with rotation and any palette remap
   * already rasterized in. Omitting it deletes any stored print file for the
   * placement — unless the design is re-sent unchanged, in which case the
   * stored one still describes this art and is kept.
   */
  printFile?: { url: string; geometry: DesignGeometry } | null;
  /**
   * Generated mockups to attach, each tagged with the color it was rendered
   * for so it persists scoped to that color's variants rather than the whole
   * product. A null color falls back to product scope. `isExtra` marks
   * additional camera angles: persisted as gallery media after the primary
   * renders, but never used as the color swatch.
   */
  mockups?: { url: string; color?: string | null; isExtra?: boolean }[];
};

/**
 * A design the user placed in the product designer, uploaded through the
 * indexed asset pipeline (`client.assets.upload`). Held in memory by the
 * designer and echoed into mockup/customization payloads.
 */
export type CommerceUploadedDesignAsset = {
  url: string;
  sizeBytes: number;
  contentType: string;
  name: string;
  /**
   * The asset row id, when known. Lets the designer read the design's bytes back
   * through bedrock-api (`assets.getContent`) for canvas-safe pixel work
   * (embroidery color detection / recolor) instead of a CORS-unreliable CDN
   * fetch.
   */
  assetId?: string | null;
};

function encodePathSegment(value: string): string {
  return encodeURIComponent(value);
}

export function createCommerceClient({ request }: { request: RequestFn }) {
  return {
    providers: {
      list: (): Promise<CommerceProvidersResult> => {
        return request<CommerceProvidersResult>({
          method: "GET",
          path: "/api/v1/commerce/providers",
        });
      },
    },
    catalog: {
      products: (
        input: CommerceCatalogProductsInput,
      ): Promise<CommerceCatalogProductsResult> => {
        return request<CommerceCatalogProductsResult>({
          method: "GET",
          path: "/api/v1/commerce/catalog/products",
          query: input,
        });
      },
      product: (input: {
        provider: string;
        providerProductId: string;
      }): Promise<CommerceCatalogProductResult> => {
        return request<CommerceCatalogProductResult>({
          method: "GET",
          path: `/api/v1/commerce/catalog/products/${encodePathSegment(
            input.provider,
          )}/${encodePathSegment(input.providerProductId)}`,
        });
      },
      sync: (
        input: CommerceCatalogSyncInput,
      ): Promise<CommerceCatalogSyncResult> => {
        const { projectId, ...body } = input;
        return request<CommerceCatalogSyncResult>({
          method: "POST",
          path: "/api/v1/commerce/catalog/sync",
          query: { projectId },
          body,
        });
      },
      /**
       * Re-sync one provider catalog product (the targeted counterpart to
       * `sync`). Used by the products app's debug overlay.
       */
      syncProduct: (input: {
        provider: string;
        providerProductId: string;
        projectId: string;
      }): Promise<{ id: string; slug: string }> => {
        const { projectId, ...body } = input;
        return request<{ id: string; slug: string }>({
          method: "POST",
          path: "/api/v1/commerce/catalog/sync/product",
          query: { projectId },
          body,
        });
      },
      /**
       * Curated garment SVG markup by file name (e.g. `tee-12-front.svg`),
       * proxied through the API because the registry CDN sends no CORS headers.
       */
      garmentSvg: (input: { file: string }): Promise<{ svg: string }> => {
        return request<{ svg: string }>({
          method: "GET",
          path: `/api/v1/commerce/catalog/garments/${encodePathSegment(
            input.file,
          )}`,
        });
      },
    },
    mockups: {
      create: (
        input: ProviderMockupRequest,
      ): Promise<CommerceMockupTaskResult> => {
        return request<CommerceMockupTaskResult>({
          method: "POST",
          path: "/api/v1/commerce/mockups",
          body: input,
        });
      },
      get: (input: {
        provider: string;
        taskKey: string;
      }): Promise<CommerceMockupTaskResult> => {
        return request<CommerceMockupTaskResult>({
          method: "GET",
          path: `/api/v1/commerce/mockups/${encodePathSegment(
            input.provider,
          )}/${encodePathSegment(input.taskKey)}`,
        });
      },
    },
    agentProducts: {
      find: (
        input: CommerceAgentProductsInput,
      ): Promise<CommerceAgentProductsResult> => {
        const { search, ...query } = input;
        return request<CommerceAgentProductsResult>({
          method: "GET",
          path: "/api/v1/commerce/agent-products",
          query: { ...query, q: search },
        });
      },
      overview: (input: {
        projectId: string;
        range: CommerceAgentProductsOverviewRange;
      }): Promise<{ overview: CommerceAgentProductsOverview }> => {
        return request<{ overview: CommerceAgentProductsOverview }>({
          method: "GET",
          path: "/api/v1/commerce/agent-products/overview",
          query: input,
        });
      },
      get: (input: {
        id: string;
      }): Promise<CommerceAgentProductDetailResult> => {
        return request<CommerceAgentProductDetailResult>({
          method: "GET",
          path: `/api/v1/commerce/agent-products/${encodePathSegment(
            input.id,
          )}`,
        });
      },
      /**
       * The project's product for a provider catalog item, or null. Matched by
       * the same rule `create` is idempotent on, so a null here means a create
       * would make a new product and a non-null is the product it would
       * reconcile instead.
       */
      getByProvenance: (input: {
        projectId: string;
        provider: string;
        providerProductId: string;
      }): Promise<{ product: CommerceAgentProductDetail | null }> => {
        return request<{ product: CommerceAgentProductDetail | null }>({
          method: "GET",
          path: "/api/v1/commerce/agent-products/by-provenance",
          query: input,
        });
      },
      getRegistryDocument: (input: {
        id: string;
      }): Promise<{ registryDocument: CatalogProduct | null }> => {
        return request<{ registryDocument: CatalogProduct | null }>({
          method: "GET",
          path: `/api/v1/commerce/agent-products/${encodePathSegment(
            input.id,
          )}/registry-document`,
        });
      },
      create: (
        input: CreateCommerceAgentProductInput & { projectId: string },
      ): Promise<CommerceCreateAgentProductResult> => {
        const { projectId, ...body } = input;
        return request<CommerceCreateAgentProductResult>({
          method: "POST",
          path: "/api/v1/commerce/agent-products",
          query: { projectId },
          body,
        });
      },
      createCustom: (
        input: CreateCustomCommerceAgentProductInput & { projectId: string },
      ): Promise<CommerceAgentProductDetailResult> => {
        const { projectId, ...body } = input;
        return request<CommerceAgentProductDetailResult>({
          method: "POST",
          path: "/api/v1/commerce/agent-products/custom",
          query: { projectId },
          body,
        });
      },
      update: (
        input: UpdateCommerceAgentProductInput,
      ): Promise<CommerceAgentProductDetailResult> => {
        return request<CommerceAgentProductDetailResult>({
          method: "PATCH",
          path: `/api/v1/commerce/agent-products/${encodePathSegment(
            input.id,
          )}`,
          body: input.patch,
        });
      },
      updateGraph: (
        input: UpdateCommerceAgentProductGraphInput,
      ): Promise<CommerceAgentProductDetailResult> => {
        return request<CommerceAgentProductDetailResult>({
          method: "PATCH",
          path: `/api/v1/commerce/agent-products/${encodePathSegment(
            input.id,
          )}/graph`,
          body: input.patch,
        });
      },
      setInventory: (
        input: SetCommerceAgentProductInventoryInput,
      ): Promise<CommerceAgentProductDetailResult> => {
        return request<CommerceAgentProductDetailResult>({
          method: "PATCH",
          path: `/api/v1/commerce/agent-products/${encodePathSegment(
            input.id,
          )}/inventory`,
          body: { variants: input.variants },
        });
      },
      replaceGraph: (
        input: ReplaceCommerceAgentProductGraphInput,
      ): Promise<CommerceAgentProductDetailResult> => {
        return request<CommerceAgentProductDetailResult>({
          method: "PUT",
          path: `/api/v1/commerce/agent-products/${encodePathSegment(
            input.id,
          )}/graph`,
          body: {
            header: input.header,
            options: input.options,
            variants: input.variants,
            ...(input.media === undefined ? {} : { media: input.media }),
            ...(input.clearedVariantExternalKeys === undefined
              ? {}
              : {
                  clearedVariantExternalKeys: input.clearedVariantExternalKeys,
                }),
          },
        });
      },
      createCustomization: (
        input: CreateCommerceAgentProductCustomizationInput,
      ): Promise<CommerceAgentProductDetailResult> => {
        return request<CommerceAgentProductDetailResult>({
          method: "POST",
          path: `/api/v1/commerce/agent-products/${encodePathSegment(
            input.productId,
          )}/customizations`,
          body: {
            placement: input.placement ?? null,
            design: input.design ?? null,
            printFile: input.printFile ?? null,
            mockups: (input.mockups ?? []).map((mockup) => ({
              url: mockup.url,
              color: mockup.color ?? null,
              isExtra: mockup.isExtra ?? false,
            })),
          },
        });
      },
      delete: (input: { id: string }): Promise<CommerceAgentProductResult> => {
        return request<CommerceAgentProductResult>({
          method: "DELETE",
          path: `/api/v1/commerce/agent-products/${encodePathSegment(
            input.id,
          )}`,
        });
      },
    },
  };
}

export type CommerceClient = ReturnType<typeof createCommerceClient>;
